import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Eye, Pencil, Plus, Search, Trash2, UserPlus, X } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidatesList,
  useCandidatesCrud,
  useStandardsList,
  useGroupsList,
  useExpedientesList,
} from "@/store/hooks";
import type { Candidate, Group } from "@/data";

type EstadoFilter = "todos" | Candidate["estado"];

const ESTADOS: { value: Candidate["estado"]; label: string; tone: "gray" | "amber" | "green" | "red" }[] = [
  { value: "inscrito", label: "Inscrito", tone: "gray" },
  { value: "en-evaluacion", label: "En evaluación", tone: "amber" },
  { value: "en-dictamen", label: "En dictamen", tone: "amber" },
  { value: "certificado", label: "Certificado", tone: "green" },
  { value: "no-competente", label: "No competente", tone: "red" },
];

const AVATAR_COLORS = ["#0e7490", "#1a2744", "#0891b2", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#475569"];

function initialsFromName(name: string): string {
  const parts = name.replace(/[.,]/g, "").trim().split(/\s+/).filter(Boolean);
  // Drop common rank prefixes when computing initials
  const ranks = new Set(["sgto", "cap", "cb", "tte", "sgto.", "cap.", "cb.", "tte.", "mtro", "lic", "dr"]);
  const useful = parts.filter((p) => !ranks.has(p.toLowerCase().replace(/[°º]/g, "")));
  const base = useful.length > 0 ? useful : parts;
  if (base.length === 1) return base[0].slice(0, 2).toUpperCase();
  return (base[0][0] + base[base.length - 1][0]).toUpperCase();
}

function pickColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function genId(): string {
  return `cand-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

interface FormState {
  id: string | null;
  nombre: string;
  rango: string;
  unidad: string;
  curp: string;
  matricula: string;
  edad: string;
  estatura: string;
  experiencia: string;
  email: string;
  telefono: string;
  estado: Candidate["estado"];
}

const emptyForm: FormState = {
  id: null,
  nombre: "",
  rango: "",
  unidad: "",
  curp: "",
  matricula: "",
  edad: "",
  estatura: "",
  experiencia: "",
  email: "",
  telefono: "",
  estado: "inscrito",
};

function candidateToForm(c: Candidate): FormState {
  return {
    id: c.id,
    nombre: c.nombre,
    rango: c.rango,
    unidad: c.unidad,
    curp: c.curp,
    matricula: c.matricula,
    edad: String(c.edad),
    estatura: c.estatura,
    experiencia: c.experiencia,
    email: c.email,
    telefono: c.telefono,
    estado: c.estado,
  };
}

export default function AdminCandidatesPage() {
  const candidates = useCandidatesList();
  const standards = useStandardsList();
  const groups = useGroupsList();
  const expedientes = useExpedientesList();
  const candidatesCrud = useCandidatesCrud();
  const logEvent = useDemoStore((s) => s.logEvent);
  const enrollments = useDemoStore((s) => s.enrollments);
  const activeRole = useDemoStore((s) => s.activeRole);

  const [query, setQuery] = useState("");
  const [estadoFilter, setEstadoFilter] = useState<EstadoFilter>("todos");
  const [standardFilter, setStandardFilter] = useState<string>("todos");
  const [unidadFilter, setUnidadFilter] = useState<string>("todos");
  const [form, setForm] = useState<FormState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Candidate | null>(null);

  const unidades = useMemo(
    () => Array.from(new Set(candidates.map((c) => c.unidad))).sort(),
    [candidates]
  );

  // Map candidato -> grupos (busca en candidatosIds de cada generación)
  const groupsByCandidate = useMemo(() => {
    const m = new Map<string, Group[]>();
    for (const g of groups) {
      for (const cid of g.candidatosIds) {
        const arr = m.get(cid) ?? [];
        arr.push(g);
        m.set(cid, arr);
      }
    }
    return m;
  }, [groups]);

  // Map candidato -> expediente principal (para ver expediente)
  const expedienteByCandidate = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of expedientes) m.set(e.candidatoId, e.id);
    return m;
  }, [expedientes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return candidates.filter((c) => {
      if (q) {
        const hay = `${c.nombre} ${c.curp} ${c.matricula} ${c.unidad}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (estadoFilter !== "todos" && c.estado !== estadoFilter) return false;
      if (unidadFilter !== "todos" && c.unidad !== unidadFilter) return false;
      if (standardFilter !== "todos") {
        const inscritos = enrollments[c.id] ?? [];
        if (!inscritos.includes(standardFilter)) return false;
      }
      return true;
    });
  }, [candidates, query, estadoFilter, unidadFilter, standardFilter, enrollments]);

  function openNew() {
    setForm({ ...emptyForm });
  }

  function openEdit(c: Candidate) {
    setForm(candidateToForm(c));
  }

  function save() {
    if (!form) return;
    const nombre = form.nombre.trim();
    if (!nombre) {
      window.alert("El nombre es obligatorio.");
      return;
    }
    if (!form.curp.trim() || form.curp.trim().length !== 18) {
      window.alert("La CURP debe tener 18 caracteres.");
      return;
    }
    const edad = parseInt(form.edad, 10);
    if (!Number.isFinite(edad) || edad < 18 || edad > 70) {
      window.alert("La edad debe estar entre 18 y 70 años.");
      return;
    }

    if (form.id) {
      candidatesCrud.update(form.id, {
        nombre,
        rango: form.rango.trim(),
        unidad: form.unidad.trim(),
        curp: form.curp.trim().toUpperCase(),
        matricula: form.matricula.trim(),
        edad,
        estatura: form.estatura.trim(),
        experiencia: form.experiencia.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        estado: form.estado,
        iniciales: initialsFromName(nombre),
      });
      logEvent({ actor: "Administrador académico", rol: activeRole, accion: "actualizó candidato", entidad: "candidate", entidadId: form.id });
    } else {
      const id = genId();
      const newCand: Candidate = {
        id,
        nombre,
        rango: form.rango.trim(),
        unidad: form.unidad.trim(),
        curp: form.curp.trim().toUpperCase(),
        matricula: form.matricula.trim(),
        edad,
        estatura: form.estatura.trim(),
        experiencia: form.experiencia.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        estado: form.estado,
        progreso: 0,
        ultimaActividad: new Date().toISOString().slice(0, 10),
        avatarColor: pickColor(id),
        iniciales: initialsFromName(nombre),
      };
      candidatesCrud.add(newCand);
      logEvent({ actor: "Administrador académico", rol: activeRole, accion: "dio de alta candidato", entidad: "candidate", entidadId: id });
    }
    setForm(null);
  }

  function doDelete() {
    if (!confirmDelete) return;
    candidatesCrud.remove(confirmDelete.id);
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "eliminó candidato", entidad: "candidate", entidadId: confirmDelete.id });
    setConfirmDelete(null);
  }

  return (
    <AppLayout breadcrumb={["Administración", "Candidatos"]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Candidatos</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
            Padrón de aspirantes — {candidates.length} registrados · {filtered.length} visibles
          </div>
        </div>
        <PrimaryButton testId="button-new-candidate" onClick={openNew}>
          <Plus size={13} /> Nuevo candidato
        </PrimaryButton>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 10 }}>
          <div style={{ position: "relative" }}>
            <Search size={14} color="var(--cc-gray-400)" style={{ position: "absolute", left: 10, top: 10 }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, CURP, matrícula o unidad…"
              data-testid="input-search-candidates"
              style={input({ paddingLeft: 30 })}
            />
          </div>
          <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value as EstadoFilter)} data-testid="filter-estado" style={input()}>
            <option value="todos">Todos los estados</option>
            {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <select value={standardFilter} onChange={(e) => setStandardFilter(e.target.value)} data-testid="filter-standard" style={input()}>
            <option value="todos">Todos los estándares</option>
            {standards.map((s) => <option key={s.codigo} value={s.codigo}>{s.codigo}</option>)}
          </select>
          <select value={unidadFilter} onChange={(e) => setUnidadFilter(e.target.value)} data-testid="filter-unidad" style={input()}>
            <option value="todos">Todas las unidades</option>
            {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--cc-gray-50)", color: "var(--cc-gray-500)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                <th style={th()}>Candidato</th>
                <th style={th()}>Unidad</th>
                <th style={th()}>Matrícula / CURP</th>
                <th style={th()}>Generación</th>
                <th style={th()}>Estado</th>
                <th style={th({ textAlign: "right" })}>Progreso</th>
                <th style={th({ textAlign: "right" })}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-500)" }}>
                    Sin candidatos que coincidan con los filtros.
                  </td>
                </tr>
              )}
              {filtered.map((c) => {
                const estado = ESTADOS.find((e) => e.value === c.estado);
                const cGroups = groupsByCandidate.get(c.id) ?? [];
                const expId = expedienteByCandidate.get(c.id);
                return (
                  <tr key={c.id} data-testid={`row-candidate-${c.id}`} style={{ borderTop: "1px solid var(--cc-gray-100)" }}>
                    <td style={td()}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: c.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                          {c.iniciales}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: "var(--cc-navy)" }}>{c.nombre}</div>
                          <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{c.rango} · {c.edad} años · {c.experiencia}</div>
                        </div>
                      </div>
                    </td>
                    <td style={td()}>
                      <div style={{ color: "var(--cc-gray-700)" }}>{c.unidad}</div>
                      <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{c.telefono}</div>
                    </td>
                    <td style={td()}>
                      <div style={{ fontFamily: "monospace", fontSize: 12 }}>{c.matricula}</div>
                      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontFamily: "monospace" }}>{c.curp}</div>
                    </td>
                    <td style={td()}>
                      {cGroups.length === 0 ? (
                        <span style={{ fontSize: 11, color: "var(--cc-gray-400)" }}>Sin generación</span>
                      ) : (
                        cGroups.map((g) => (
                          <div key={g.id} style={{ fontSize: 12 }}>
                            <Link href={`/admin/grupos/${g.id}`} style={{ color: "var(--cc-teal)" }} data-testid={`link-group-${g.id}-from-${c.id}`}>
                              {g.estandar} · {g.generacion}
                            </Link>
                          </div>
                        ))
                      )}
                    </td>
                    <td style={td()}>
                      <Badge tone={estado?.tone ?? "gray"}>{estado?.label ?? c.estado}</Badge>
                    </td>
                    <td style={td({ textAlign: "right" })}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 60, height: 6, background: "var(--cc-gray-100)", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${c.progreso}%`, height: "100%", background: c.progreso === 100 ? "#16a34a" : "var(--cc-teal)" }} />
                        </div>
                        <span style={{ fontSize: 11, color: "var(--cc-gray-500)", minWidth: 28, textAlign: "right" }}>{c.progreso}%</span>
                      </div>
                    </td>
                    <td style={td({ textAlign: "right" })}>
                      <div style={{ display: "inline-flex", gap: 4 }}>
                        {expId && (
                          <Link href={`/auditor/expediente/${expId}`} title="Ver expediente" data-testid={`button-view-${c.id}`}>
                            <button style={iconBtn()} aria-label="Ver expediente"><Eye size={14} /></button>
                          </Link>
                        )}
                        <button style={iconBtn()} onClick={() => openEdit(c)} title="Editar" aria-label="Editar" data-testid={`button-edit-${c.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button style={iconBtn({ color: "#dc2626" })} onClick={() => setConfirmDelete(c)} title="Eliminar" aria-label="Eliminar" data-testid={`button-delete-${c.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {form && (
        <CandidateModal
          form={form}
          onChange={setForm}
          onClose={() => setForm(null)}
          onSave={save}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          candidate={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={doDelete}
        />
      )}
    </AppLayout>
  );
}

function CandidateModal({ form, onChange, onClose, onSave }: {
  form: FormState;
  onChange: (f: FormState) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const isEdit = !!form.id;
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--cc-gray-100)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--cc-teal-bg)", color: "var(--cc-teal)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <UserPlus size={16} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>{isEdit ? "Editar candidato" : "Nuevo candidato"}</div>
              <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>Datos personales y de contacto del aspirante</div>
            </div>
          </div>
          <button onClick={onClose} style={iconBtn()} aria-label="Cerrar"><X size={16} /></button>
        </div>
        <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Nombre completo *" full>
            <input value={form.nombre} onChange={(e) => onChange({ ...form, nombre: e.target.value })} style={input()} data-testid="input-nombre" placeholder="Sgto. 1° Jorge Ramírez Pacheco" />
          </Field>
          <Field label="Rango / Grado">
            <input value={form.rango} onChange={(e) => onChange({ ...form, rango: e.target.value })} style={input()} data-testid="input-rango" placeholder="Sargento 1°" />
          </Field>
          <Field label="Unidad / Adscripción">
            <input value={form.unidad} onChange={(e) => onChange({ ...form, unidad: e.target.value })} style={input()} data-testid="input-unidad" placeholder="Guardia Nacional — División Caminos" />
          </Field>
          <Field label="CURP *">
            <input value={form.curp} onChange={(e) => onChange({ ...form, curp: e.target.value.toUpperCase() })} style={input({ fontFamily: "monospace" })} maxLength={18} data-testid="input-curp" placeholder="18 caracteres" />
          </Field>
          <Field label="Matrícula">
            <input value={form.matricula} onChange={(e) => onChange({ ...form, matricula: e.target.value })} style={input({ fontFamily: "monospace" })} data-testid="input-matricula" placeholder="GN-204815" />
          </Field>
          <Field label="Edad *">
            <input type="number" value={form.edad} onChange={(e) => onChange({ ...form, edad: e.target.value })} style={input()} min={18} max={70} data-testid="input-edad" />
          </Field>
          <Field label="Estatura">
            <input value={form.estatura} onChange={(e) => onChange({ ...form, estatura: e.target.value })} style={input()} data-testid="input-estatura" placeholder="1.78 m" />
          </Field>
          <Field label="Experiencia" full>
            <input value={form.experiencia} onChange={(e) => onChange({ ...form, experiencia: e.target.value })} style={input()} data-testid="input-experiencia" placeholder="12 años en motocicleta" />
          </Field>
          <Field label="Correo institucional">
            <input value={form.email} onChange={(e) => onChange({ ...form, email: e.target.value })} style={input()} data-testid="input-email" placeholder="nombre@gn.gob.mx" />
          </Field>
          <Field label="Teléfono">
            <input value={form.telefono} onChange={(e) => onChange({ ...form, telefono: e.target.value })} style={input()} data-testid="input-telefono" placeholder="55 1234 5678" />
          </Field>
          <Field label="Estado">
            <select value={form.estado} onChange={(e) => onChange({ ...form, estado: e.target.value as Candidate["estado"] })} style={input()} data-testid="input-estado">
              {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--cc-gray-100)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={ghostBtn} data-testid="button-cancel">Cancelar</button>
          <PrimaryButton onClick={onSave} testId="button-save-candidate">{isEdit ? "Guardar cambios" : "Crear candidato"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ candidate, onCancel, onConfirm }: {
  candidate: Candidate;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div style={overlay} onClick={onCancel}>
      <div style={{ ...modal, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 8 }}>Eliminar candidato</div>
          <div style={{ fontSize: 13, color: "var(--cc-gray-700)", lineHeight: 1.5 }}>
            ¿Seguro que deseas eliminar a <strong>{candidate.nombre}</strong> del padrón? Esta acción no se puede deshacer y perderás su historial de inscripciones.
          </div>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--cc-gray-100)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={ghostBtn} data-testid="button-cancel-delete">Cancelar</button>
          <button onClick={onConfirm} style={{ ...ghostBtn, background: "#dc2626", color: "white", border: "1px solid #dc2626" }} data-testid="button-confirm-delete">Eliminar</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function input(extra: React.CSSProperties = {}): React.CSSProperties {
  return { width: "100%", padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13, background: "white", ...extra };
}

function th(extra: React.CSSProperties = {}): React.CSSProperties {
  return { padding: "10px 14px", textAlign: "left", fontWeight: 600, ...extra };
}

function td(extra: React.CSSProperties = {}): React.CSSProperties {
  return { padding: "12px 14px", verticalAlign: "middle", ...extra };
}

function iconBtn(extra: React.CSSProperties = {}): React.CSSProperties {
  return { width: 28, height: 28, border: "1px solid var(--cc-gray-200)", background: "white", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--cc-gray-700)", ...extra };
}

const ghostBtn: React.CSSProperties = {
  padding: "8px 14px",
  background: "white",
  border: "1px solid var(--cc-gray-200)",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  color: "var(--cc-gray-700)",
  cursor: "pointer",
};

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
  padding: 20,
};

const modal: React.CSSProperties = {
  background: "white",
  borderRadius: 12,
  width: "100%",
  maxWidth: 720,
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
};
